import { useCallback, useEffect, useMemo } from 'react';
import { format, isToday, isTomorrow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';

import type { IUpcomingSchedule, IUpcomingShift } from '@/components/agent/dashboard/types';

import { useAppSelector, useAppDispatch, useTimezone } from '@/hooks';
import { FULL_MONTH_DAY_FORMAT, MONTH_DAY_FORMAT } from '@/config/variables';
import { selectSchedulesList, selectUpcomingShifts } from '@/store/selectors/scheduleSelectors';
import { getUpcomingShifts } from '@/store/slices/scheduleSlice';
import { getBlockSettings } from '@/helpers/block';

interface IReturn {
  schedule: IUpcomingSchedule;
}

const upcomingShiftsSelector = createSelector(
  selectUpcomingShifts,
  selectSchedulesList,
  (upcomingShiftsRaw, schedulesList) => ({
    upcomingShiftsRaw,
    schedulesList,
  }),
);

const useUpcomingShifts = (): IReturn => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { upcomingShiftsRaw, schedulesList } = useAppSelector(upcomingShiftsSelector);
  const { timezoneName } = useTimezone();

  const getShiftTitle = useCallback(
    (startAt: Date): string => {
      if (isToday(startAt)) return t`common.today`;
      if (isTomorrow(startAt)) return `${t`common.tomorrow`}, ${format(startAt, MONTH_DAY_FORMAT)}`;
      return format(startAt, FULL_MONTH_DAY_FORMAT);
    },
    [t],
  );

  /**
   * @param rawUpcomingShifts - array of upcoming shifts, when there is no shifts the api response is an empty string
   */
  const upcomingShifts: IUpcomingSchedule = useMemo(() => {
    if (schedulesList.length === 0 || upcomingShiftsRaw.length === 0) return {};

    const dateNow = Date.now();
    const shifts: IUpcomingShift[] = schedulesList
      .flatMap(schedule =>
        schedule.shifts.flatMap(shift => {
          const upcoming = upcomingShiftsRaw.find(upcomingShift => upcomingShift.shiftId === shift.shiftId);
          if (!upcoming) return [];
          const { startAt, endAt } = getBlockSettings(upcoming.startBlock, upcoming.endBlock, timezoneName);

          const parsedShift: IUpcomingShift = {
            shiftId: upcoming.shiftId,
            title: getShiftTitle(startAt),
            scheduleName: schedule.scheduleName,
            clientName: schedule.clientName,
            startTime: startAt,
            endTime: endAt,
          };
          return [parsedShift];
        }),
      )
      .filter(shift => shift.startTime.getTime() > dateNow);

    return shifts
      .sort((a, b) => {
        return a.startTime.getTime() > b.startTime.getTime() ? 1 : -1;
      })
      .reduce((acc, schedule) => {
        const { title } = schedule;
        acc[title] = acc[title] ?? [];
        acc[title].push(schedule);
        return acc;
      }, {} as IUpcomingSchedule);
  }, [getShiftTitle, schedulesList, timezoneName, upcomingShiftsRaw]);

  useEffect(() => {
    const getUpcomingShiftsRequest = dispatch(getUpcomingShifts());

    return () => {
      getUpcomingShiftsRequest.abort();
    };
  }, []);

  return {
    schedule: upcomingShifts,
  };
};

export { useUpcomingShifts };
